import SoftButton from "components/SoftButton";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useSelector } from "react-redux";

export const DeleteFlowDialog = ({ open, onAccept, onClose, flow, flowUsage, sharedUsers, isPermanentDelete }) => {
   const { integrations } = useSelector((state) => state.portal.integrations);

   const getBlockingReasons = () => {
      if (!open) return [];

      const reasons = [];

      if (flowUsage?.isCheckFailed) {
         reasons.push({
            type: "checkFailed",
            message: "Its usage could not be verified. Please try again.",
            flows: []
         });
      }

      if (flowUsage?.isShared || sharedUsers?.length > 0) {
         reasons.push({
            type: "shared",
            message: "It is currently shared with other users. Please unshare it first.",
            flows: []
         });
      }

      if (flowUsage?.isScheduled) {
         reasons.push({
            type: "scheduled",
            message: "It is currently scheduled to execute. Please unschedule it first.",
            flows: []
         });
      }

      const invokeFlows = flowUsage?.invokeFlows;
      if (invokeFlows?.length > 0) {
         reasons.push({
            type: "invoke",
            message: `It is currently being used by an invoke component in the following ${invokeFlows.length > 1 ? "flows" : "flow"}:`,
            flows: invokeFlows
         });
      }

      return reasons;
   };

   const getFlowIntegrations = () => {
      if (!flow?.integrations) return [];
      const flowIntegrations = JSON.parse(flow.integrations);
      return (Array.isArray(integrations) ? integrations : []).filter((integration) => flowIntegrations.includes(integration.id));
   };

   const blockingReasons = getBlockingReasons();
   const canDelete = blockingReasons.length === 0;
   const flowIntegrations = canDelete ? getFlowIntegrations() : [];

   const renderBlockingReasons = () => (
      <>
         <strong>{flow.name}</strong> cannot be deleted. Please resolve the following {blockingReasons.length === 1 ? "issue" : "issues"} before proceeding:
         <ul style={{ marginTop: "8px", marginLeft: "1rem" }}>
            {blockingReasons.map((reason) => (
               <li key={reason.type} style={{ marginBottom: "4px" }}>
                  {reason.message}
                  {reason.flows.length > 0 && (
                     <ul style={{ marginTop: "4px", marginLeft: "1rem" }}>
                        {reason.flows.map((f) => (
                           <li key={f}>
                              <strong>{f}</strong>
                           </li>
                        ))}
                     </ul>
                  )}
               </li>
            ))}
         </ul>
      </>
   );

   const renderDeleteConfirmation = () => (
      <div>
         <div>
            Are you sure you want to delete <strong>{flow.name}</strong>?
         </div>
         {flowIntegrations.length === 1 && (
            <div style={{ marginBottom: "1rem" }}>
               This flow is currently using the <strong>{flowIntegrations[0].integration_name}</strong> integration.
            </div>
         )}
         {flowIntegrations.length > 1 && (
            <>
               <div style={{ marginBottom: "1rem" }}>This flow is currently using multiple integrations listed below:</div>
               <ul style={{ paddingLeft: "1rem" }}>
                  {flowIntegrations.map((integration, index) => (
                     <li key={`${index}-${integration.id}`}>
                        <strong>{integration.integration_name}</strong>
                     </li>
                  ))}
               </ul>
            </>
         )}
      </div>
   );

   return (
      <Dialog open={open} onClose={onClose} fullWidth>
         <DialogTitle>{isPermanentDelete ? "Delete Permanently" : "Delete Flow"}</DialogTitle>
         <DialogContent>{open && (canDelete ? renderDeleteConfirmation() : renderBlockingReasons())}</DialogContent>
         <DialogActions>
            <SoftButton onClick={onClose} color="primary" variant="outlined">
               {canDelete ? "Cancel" : "Close"}
            </SoftButton>
            {canDelete && (
               <SoftButton color="info" onClick={onAccept}>
                  Delete
               </SoftButton>
            )}
         </DialogActions>
      </Dialog>
   );
};
